import React from 'react';
import type {
  CashflowPredictionSnapshot,
  ForecastScenarioDefinition,
  ForecastScenarioKey,
} from '../types';
import { formatAmountCurrency } from '../utils';

const scenarioColorMap: Record<ForecastScenarioKey, string> = {
  base: '#111827',
  opt: '#2563eb',
  pess: '#f97316',
};

const scenarioChipBaseStyle = {
  padding: '4px 10px 4px 7px',
  borderRadius: '12px',
  borderWidth: '1.5px',
  borderStyle: 'solid',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  cursor: 'pointer',
  userSelect: 'none' as const,
  appearance: 'none' as const,
  WebkitAppearance: 'none' as const,
  boxShadow: 'none',
  outline: 'none',
};

interface ScenarioSectionProps {
  prediction?: CashflowPredictionSnapshot;
  scenarioDefinitions: ForecastScenarioDefinition[];
  activeScenario: ForecastScenarioKey;
  onScenarioChange: (scenario: ForecastScenarioKey) => void;
}

const metricTitleMap = {
  inflow: '💵 预测期总流入',
  outflow: '📤 预测期总流出',
  netCashflow: '📊 净现金流',
  endingBalance: '🏦 期末现金余额',
};

const toneClassMap = {
  positive: 'text-[#059669]',
  negative: 'text-[#dc2626]',
  neutral: 'text-[#0f172a]',
};

const emptyMetricDescriptionMap = {
  inflow: '待预测 · 含合同回款 + 商机加权 + 借款/融资',
  outflow: '待预测 · 含刚性支出 + 应付采购费 + 税费 + 银行本息还款等',
  netCashflow: '待预测 · 预测期累计',
  endingBalance: '待预测 · 预测窗口截止日',
};

const ScenarioSection: React.FC<ScenarioSectionProps> = ({
  prediction,
  scenarioDefinitions,
  activeScenario,
  onScenarioChange,
}) => {
  const activeMetrics = prediction?.scenarioMetrics[activeScenario];
  const activeScenarioLabel =
    scenarioDefinitions.find(item => item.key === activeScenario)?.label ||
    '中性';

  return (
    <section className="mt-[16px]">
      <div className="mb-[12px] flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-2 text-[13px] font-semibold text-[#0f172a]">
            情景模拟
          </span>
          {scenarioDefinitions.map(item => {
            const active = item.key === activeScenario;
            return (
              <div
                key={item.key}
                role="button"
                tabIndex={prediction ? 0 : -1}
                className="transition-colors hover:border-[#9ca3af]"
                style={{
                  ...scenarioChipBaseStyle,
                  borderColor: active ? 'transparent' : '#e5e7eb',
                  backgroundColor: active
                    ? scenarioColorMap[item.key]
                    : '#ffffff',
                  color: active ? '#ffffff' : '#475569',
                  opacity: prediction ? 1 : 0.55,
                  pointerEvents: prediction ? 'auto' : 'none',
                }}
                onClick={() => onScenarioChange(item.key)}
                onKeyDown={event => {
                  if (!prediction) {
                    return;
                  }
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onScenarioChange(item.key);
                  }
                }}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: active ? '#ffffff' : item.color }}
                />
                <span>{item.label}</span>
              </div>
            );
          })}
        </div>
        <div
          className="text-[10.5px]"
          style={{ color: prediction ? '#94a3b8' : '#cbd5e1' }}
        >
          {prediction
            ? '切换中性 / 乐观 / 悲观，仅影响下方四卡数据'
            : '⏸ 待预测后生效 · 切换中性 / 乐观 / 悲观，仅影响下方四卡数据'}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {(
          Object.keys(metricTitleMap) as Array<keyof typeof metricTitleMap>
        ).map(metricKey => {
          const metric = activeMetrics?.[metricKey];
          const toneClass = metric
            ? toneClassMap[metric.tone || 'neutral']
            : 'text-[#cbd5e1]';

          return (
            <article
              key={metricKey}
              className="min-h-[96px] rounded-[9px] border border-solid border-[#e5e7eb] bg-white px-4 py-[14px] shadow-[0_1px_4px_rgba(15,23,42,0.05)]"
            >
              <div className="text-[12px] font-medium text-[#6b7280]">
                {metricTitleMap[metricKey]}
              </div>
              <div
                className={`mt-[8px] text-[20px] font-semibold leading-[1.15] ${toneClass}`}
              >
                {metric ? formatAmountCurrency(metric.valueWan * 10000) : '¥ ——'}
              </div>
              <div className="mt-1 text-[11px] leading-[1.45] text-[#6b7280]">
                {metric
                  ? `${activeScenarioLabel}情景 · ${metric.description}`
                  : emptyMetricDescriptionMap[metricKey]}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default ScenarioSection;
